/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Appearance from "App/geometry/material/appearance";
import LinkedShapesListItem from "App/render/webgl/utils/linked_shapes_list_item";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import {logInfo} from "App/debug";
import Model from "App/model";
import {RenderElement} from "App/model/elements/element";
import TextureDataHandler from "App/utils/textures/texture_data_handler";
import RenderOption from "App/geometry/material/render_option";
import {mat4, vec3} from "gl-matrix";
import ModelDescription from "App/model/description";
import BoundingBox from "App/utils/bounding_box";
import RenderNode from "App/render/webgl/nodes/render_node";
import {UPDATETYPES} from "App/render/webgl/renderables/batches/batch";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import TextureNode from "App/render/webgl/nodes/texture_node";
import {LineTypeName, OutlineTypeName} from "App/geometry/objects/type_names";
import {DataResourceObserver} from "App/storage/data_resource_observer";
import {DataResource} from "App/storage/data_resource";

/**
 * @extends {DataResourceObserver}
 * @constructor
 */
export default
class RenderObject extends DataResourceObserver {

    public appearance: Appearance = null;
    public geometry: AbstractGeometry = null;

    protected _classTypeName: string = null;

    protected model: Model = null;
    protected properties: any = {affectBoundingBox: true, contributeToNearPlaneClipping: true, canCastShadow: true}; // default properties

    protected _renderGraphObject: ShapeBatchNode = null;
    protected _textureDataHandler: TextureDataHandler = null;

    protected _placement: LinkedShapesListItem = new LinkedShapesListItem(this);

    // start counting from 1, the 0 value is used to identify that e.g. no object is picked
    protected UID: number;

    protected _elementRef: RenderElement = null;

    protected _onModelLoadTransformationFlag: boolean = false;

    protected _inrednergraph: number = 0;
    protected _forceHidden: boolean = false;

    constructor(aUID: number) {
        super();

        this.UID = aUID;
    }

    public getProperties(): any {
        return this.properties;
    }

    public setModel(aModel: Model) {
        this.model = aModel;
    }

    public setNeedUpdateAll() {
        for (let key in UPDATETYPES) {
            this._placement.updates[UPDATETYPES[key]] = true;
        }

        if (this._renderGraphObject) {
            this._renderGraphObject.setNeedUpdate();
        }
    }

    public setNeedUpdate(aUpdates: UPDATETYPES[]) {
        for (let update of aUpdates) {
            this._placement.updates[update] = true;
        }

        if (this._renderGraphObject) {
            this._renderGraphObject.setNeedUpdate();
        }
    }

    public setOnLoadTransformation(aValue: boolean) {
        this._onModelLoadTransformationFlag = aValue;
    }

    public getModel(): Model {
        return this.model;
    }

    public getUID(): number {
        return this.UID;
    }

    /***
     * Return type parsed from input geometryDescription.json file
     */
    public getType(): string {
        return this.properties.type;
    }

    /***
     * Return class type name. It is set in Factory.
     * @returns {null}
     */
    public getClassTypeName(): string {
        return this._classTypeName;
    }

    public getBinaryDataSize(): number {
        return this.geometry.getBinaryDataSize();
    }

    public getVisibility(): boolean {
        if (!this._elementRef) {
            return false;
        }

        if (this._forceHidden !== undefined && this._forceHidden) {
            return false;
        }

        return this._elementRef.getVisibility();
    }

    public getElement(): RenderElement  {
        return this._elementRef;
    }

    public getDownloadPriority(): number {
        return this._elementRef.getDownloadPriority();
    }

    public getIsSelected(): boolean {
        if (!this._elementRef) {
            return false;
        }

        return this._elementRef.getIsSelected();
    }

    /***
     * The manipulaton is based on the render model at this moment so it is te smallest unit which can be manipulated now.
     * @returns {boolean}
     */
    public getIsManipulated(): boolean {
        if (!this.model) {
            return false;
        }

        return this.model.isManipulated();
    }

    public hasTexture(): boolean {
        return this.appearance.hasTexture(null);
    }

    public hasNormalTexture(): boolean {
        return false;
    }

    public isOpaque(): boolean {
        return this.appearance.isOpaque();
    }

    public isOutline(): boolean {
        return (
            this._classTypeName === OutlineTypeName ||
            (this._classTypeName === LineTypeName &&
                (this.appearance && this.appearance.hasOwnProperty("lineProperties")
                    && this.appearance.lineProperties && this.appearance.lineProperties.getLinewidthScaleFactor() === 1.0))
        );
    }

    public hasAlphaTransparency(): boolean {
        return this.appearance.hasAlphaTransparency();
    }

    public attachToTextureDataHandler(aTextureDataHandler: TextureDataHandler) {
        if (this._textureDataHandler) {
            logInfo("attaching texture data handler to shape " + this.getUID() + " which already has one");
        }
        this._textureDataHandler = aTextureDataHandler;
    }

    public getTextureDataHandler(): TextureDataHandler {
        return this._textureDataHandler;
    }

    public detachFromTextureDataHandler() {
        this._textureDataHandler = null;
    }

    public attachToRenderGraphObject(aRenderGraphObject: RenderNode) {
        if (aRenderGraphObject !== null) {
            this._inrednergraph++;
            if (this._inrednergraph > 1) {
                logInfo("shape " + this.UID + " added to render graph more times, expected problems");
            }
        }
        else {
            this._inrednergraph--;
        }

        this._renderGraphObject = aRenderGraphObject as ShapeBatchNode;
    }

    public detachFromRenderGraphObject() {
        this._renderGraphObject = null;
        if (this._inrednergraph > 0) {
            this._inrednergraph--;
        } else {
            logInfo("detaching shape " + this.UID + " which is not attached to render graph, expected problems");
        }
    }

    public getRenderGraphObject(): RenderNode {
        return this._renderGraphObject;
    }

    public getRenderOptions(): RenderOption {
        return this.appearance.renderOption;
    }

    // TODO: We should ask the render graph whether the shape is ready to be rendered
    public getIsReadyToRender(): boolean {
        let geometryReady = this._placement.buffer.vertexOffset > -1;
        let textureReady = true;
        if (this.hasTexture()) {
            let textureNode = this._renderGraphObject.findTextureNode() as TextureNode;
            if (textureNode) {
                textureReady = (/*!textureNode.isSwitching() && */textureNode.getRenderable().isReady());
            }
        }

        return geometryReady && textureReady;
    }

    public getPlacement(): LinkedShapesListItem {
        return this._placement;
    }

    public updatePositions(aPositions: number[]) {
        // update positions and recompute bounding box of this object
        this.geometry.updatePositions(aPositions);

        this.setNeedUpdate([
            UPDATETYPES.VERTEX,
            UPDATETYPES.NONINDEXED_VERTEX
        ]);

        this.model.getDocument().getRenderer().updateShapeBBox(this);
    }

    public removed() {
        this.appearance.unrefMaterials();

        // reset placement
        this._placement = new LinkedShapesListItem(this);

        if (this._renderGraphObject) {
            this._renderGraphObject.scheduleReCreateGLBuffers();
        }
    }

    public getPositionAttributeSize(): number {
        // this value sign that each vertex is defined by default via vec3 attribute (but e.g. svSideShape has vec4 attribute type)
        return 3;
    }

    public getPrimitiveSize(): number {
        throw new Error("Object.getPrimitiveSize method must be implemented!");
    }

    public affectBoundingBox(): boolean {
        return this.properties.affectBoundingBox;
    }

    public canCastShadow(): boolean {
        if ((this._elementRef && !this._elementRef.getCanCastShadow()) || (this.appearance && this.appearance.surfaceColor.transparency > 0.2)) {
            return false;
        }

        return this.properties.canCastShadow;
    }

    public getVerticesCount(): number {
        throw new Error("Object.getVerticesCount method must be implemented!");
    }

    public getAttachedBoundingBoxes(): any[] {
        return [[this.getUID(), this.properties.bboxMin, this.properties.bboxMax]];
    }

    public getBoundingBoxMin(): vec3 | number[] {
        return this.properties.bboxMin;
    }

    public getBoundingBoxMax(): vec3 | number[] {
        return this.properties.bboxMax;
    }

    /**
     * To solve problems with adding large terrain which disallow us to go close to some building in the center because
     * the computation of near plane clipping value is based on scene bounding box. So here we count with only the
     * shapes which can contribute to the bbox for near plane computation.
     *
     */
    public getBoundingBoxMinForNPC(): number[] {
        if (!this.properties.contributeToNearPlaneClipping) {
            return null;
        }

        return this.properties.bboxMin;
    }

    public getBoundingBoxMaxForNPC(): number[] {
        if (!this.properties.contributeToNearPlaneClipping) {
            return null;
        }

        return this.properties.bboxMax;
    }

    /***
     * Apply the transformation to the bounding box property and the positions in mesh
     * @param aTransformation
     */
    public transform(aTransformation: mat4) {
        this.geometry.transform(aTransformation);

        this.setNeedUpdate([
            UPDATETYPES.VERTEX,
            UPDATETYPES.NORMAL,
            UPDATETYPES.POSITION_OFFSET,
            UPDATETYPES.NONINDEXED_VERTEX
        ]);

        this.model.getDocument().getRenderer().updateShapeBBox(this);
    }

    /**
     * Parse geometry object from JSON and if aRenderElement is defined, then instead of searching of reference to model
     * object via model.getElementsByShapeID, the direct reference to the aRenderElement is used
     * (this applies for objects created via the sceneAPI)
     *
     * @param {json} aShapeJSON Model geometry description in JSON
     * @param {object} aRenderElement  A reference to model element which contains description of the logical object (i.e. Lexocad element)
     */
    public parse(aModelDescription: ModelDescription, aShapeJSON: any, aRenderElement: RenderElement) {
        this._elementRef = aRenderElement;

        // deep copy of object
        let props = JSON.parse(JSON.stringify(aShapeJSON.description));

        // extend the parsed properties with current properties
        for (let member in this.properties) {
            if (props[member] === undefined) {
                props[member] = this.properties[member];
            }
        }

        if (props.id === undefined) {
            props.id = "computed";
        }

        this.properties = props;

        // TODO: let create appearance to the material library
        // we need to parse appearance before we call the this._elementRef.addGeometryObject as we need to know if the object has texture and its resolution
        this.appearance = new Appearance(this.model.getMaterialLibrary());
        this.appearance.parse(aModelDescription, aShapeJSON.appearance);

        this.appearance.refMaterials();

        if (this._elementRef) {
            this._elementRef.addGeometryObject(this);

            // apply repetition transformation
            let repetitionTrafo = this._elementRef.getRepetitionTransformation();
            if (repetitionTrafo) {
                let bbox = new BoundingBox(this.properties.bboxMin, this.properties.bboxMax);
                bbox.transformMat4(new Float32Array(repetitionTrafo));

                let bboxMin = vec3.clone(bbox.getMin());
                let bboxMax = vec3.clone(bbox.getMax());
                this.properties.bboxMin = [bboxMin[0], bboxMin[1], bboxMin[2]];
                this.properties.bboxMax = [bboxMax[0], bboxMax[1], bboxMax[2]];
            }
        }
    }

    public getAttachedDataResources(): DataResource[] {
        let resources = DataResourceObserver.prototype.getAttachedDataResources.call(this);
        if (this._textureDataHandler) {
            resources = resources.concat(this._textureDataHandler.getAttachedDataResources());
        }

        return resources;
    }

    public getJSONRepresentation(): any {
        let result = {
            description: {
                id: this.properties.id,
                type: this.properties.type,
                bboxMin: [this.properties.bboxMin[0], this.properties.bboxMin[1], this.properties.bboxMin[2]],
                bboxMax: [this.properties.bboxMax[0], this.properties.bboxMax[1], this.properties.bboxMax[2]],
                contributeToNearPlaneClipping: this.properties.contributeToNearPlaneClipping
            },
            appearance: this.appearance.getJSONRepresentation()
        };

        return result;
    }

    public saveToDOM(): any {
        return null;
    }

    public dataDownloadAborted(aStatus: any, aResource: DataResource) {
    }

    public dataDownloadFailed(aStatus: any, aResource: DataResource) {
    }

    public dataDownloaded(aStatus: any, aResource: DataResource) {
    }

    public getLoadDataQuery(): any {
    }
}
